import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { Flag } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

interface GetAlertsResponse {
  flags: Flag[];
}

export const useAlerts = (patientId?: string) => {
  return useApi<GetAlertsResponse, Flag[]>({
    queryOptions: {
      enabled: !!patientId,
      queryKey: QueryKeys.Patient.MedicalRecords.Alerts.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get("/charting/flag/search", {
          params: {
            patientId,
          },
        });
        return response?.data;
      },
      select: (data: GetAlertsResponse) => {
        return data?.flags?.map((flag) => new Flag(flag));
      },
    },
  });
};

interface AddAlertsParams {
  patientId: string;
  onSuccess: () => void;
}

interface AddFlagsRequest {
  flags: string[];
}

interface AddFlagsResponse {
  flags: Flag[];
}

export const useAddAlerts = ({ patientId, onSuccess }: AddAlertsParams) => {
  const queryClient = useQueryClient();

  const putFlags = async (flagCodes: string[]) => {
    const response = await axiosInstance.put("/charting/flag/add", {
      patientId,
      flagCodes,
    });
    return response.data;
  };

  return useApiMutation<AddFlagsResponse, AddFlagsRequest>({
    mutationOptions: {
      mutationKey: ["patient", "flag", "add"],
      mutationFn: async ({ flags }) => {
        return putFlags(flags);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Alerts.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};

interface RemoveAlertsParams {
  patientId: string;
  onSuccess: () => void;
}

interface RemoveFlagsRequest {
  flags: string[];
}

interface RemoveFlagsResponse {
  flags: Flag[];
}

export const useRemoveAlerts = ({
  patientId,
  onSuccess,
}: RemoveAlertsParams) => {
  const queryClient = useQueryClient();

  const postFlags = async (flagCodes: string[]) => {
    const response = await axiosInstance.post("/charting/flag/remove", {
      patientId,
      flagCodes,
    });
    return response.data;
  };

  return useApiMutation<RemoveFlagsResponse, RemoveFlagsRequest>({
    mutationOptions: {
      mutationKey: ["patient", "flag", "remove"],
      mutationFn: async ({ flags }) => {
        return postFlags(flags);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.MedicalRecords.Alerts.DEFAULT(patientId),
        });
        onSuccess?.();
      },
    },
  });
};
